import { useContext, useEffect, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { Context } from "../../../../context";
import { useNavigate } from "react-router-dom";
import { Layout } from "../../../../containers/layout/dashboard";
import { SystemUsersWrapper } from "./styled";
import { BaseButton } from "../../../../components/button/styled";
import AddIcon from "@mui/icons-material/Add";
import { BaseTable } from "../../../../components/table";
import { BaseFieldSet } from "../../../../components/formfields/fieldset/styled";
import { BaseLabel } from "../../../../components/formfields/label/styled";
import { BaseInput } from "../../../../components/formfields/input/styled";
import { SearchIcon } from "../../../../assets";
import { retrieveAllUsersService } from "../../../../utils/apis/usermanagement/user/retrieveAllUsers";
import Cookies from "universal-cookie";
import { retrieveRoleByIdService } from "../../../../utils/apis/usermanagement/role/retrieveRoleById";
import { BaseConfirmationModal } from "../../../../components/modals/confirmation";
import { deleteUserService } from "../../../../utils/apis/usermanagement/user/deleteUser";
import { useQuery } from "@tanstack/react-query";

export const SystemUsers = () => {
	const headers = ["First Name", "Last Name", "Role", "Action"];

	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const navigate = useNavigate();
	const { setActiveNavItem } = useContext(Context);

	const [filter, setFilter] = useState<string>("");
	const [selectedUserId, setSelectedUserId] = useState<string>("");
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

	useEffect(() => {
		setActiveNavItem("User Management");
	}, [setActiveNavItem]);

	const { data: filteredUsers, refetch: refetchFilteredUsers } = useQuery({
		queryKey: ["filteredUsers", TOKEN, filter],
		queryFn: async () => {
			const users = await retrieveAllUsersService(TOKEN, filter);
			const transformedUsers = await Promise.all(
				users?.map(async (user: any) => {
					const lastestRoleId = user?.roles?.[user.roles.length - 1];
					if (!lastestRoleId) {
						return {
							...user,
							roles: [],
						};
					}
					const role = await retrieveRoleByIdService(lastestRoleId, TOKEN);
					return {
						...user,
						roles: [role],
					};
				})
			);
			return transformedUsers;
		},
		enabled: !!TOKEN,
	});

	const handleConfirmationModalPersist = () => {
		return setIsConfirmationModalOpen(true);
	};

	const handleCancelClick = () => {
		// reset error to default
		setError(null);
		return setIsConfirmationModalOpen(false);
	};

	const handleNavigateToAddSystemUserForm = () => {
		return navigate("/usermanagement/systemusers/addsystemuser");
	};

	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const value = e.target.value;
		setFilter(value);
	};

	const handleDeleteSystemUser = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		id: string
	) => {
		e.preventDefault();
		setError(null);
		setIsLoading(true);
		try {
			const response = await deleteUserService(id, TOKEN);
			if (response.status === "success") {
				setIsLoading(false);
				setIsConfirmationModalOpen(false);
				refetchFilteredUsers();
			} else {
				setIsLoading(false);
				setError("User deletion failed. Please try again.");
			}
		} catch (error: any) {
			setIsLoading(false);
			setError(`Role deletion failed. ${error.message}`);
			console.error("Role deletion failed:", error);
		}
	};

	const handleCallToActionClick = async (
		e:
			| React.MouseEvent<HTMLButtonElement, MouseEvent>
			| React.MouseEvent<HTMLSpanElement, MouseEvent>,
		id: string,
		action?: string
	) => {
		if (action === "edit") {
			return navigate(`/usermanagement/systemusers/editsystemuser/${id}`);
		}
		if (action === "delete") {
			setSelectedUserId(id);
			return setIsConfirmationModalOpen(true);
		}
	};

	return (
		<Layout id="system-users">
			<SystemUsersWrapper>
				<BaseConfirmationModal
					error={error}
					isLoading={isLoading}
					open={isConfirmationModalOpen}
					className="delete-user-confirmation-modal"
					body="Once deleted, you will not be able to recover this information!"
					handleClose={handleConfirmationModalPersist}
					handleConfirm={(e) => handleDeleteSystemUser(e, selectedUserId)}
					handleCancel={handleCancelClick}
				/>
				<Stack
					gap={{
						mobile: "calc(var(--flex-gap)/2)",
						miniTablet: "var(--flex-gap)",
					}}
					alignItems={{ miniTablet: "center" }}
					justifyContent={"space-between"}
					direction={{ miniTablet: "row" }}
					overflow={"hidden"}
				>
					<Box>
						<Typography
							variant="h1"
							fontFamily={"Inter"}
							fontWeight={700}
							fontSize={24}
							lineHeight={"normal"}
							color={"#373737"}
						>
							System Users
						</Typography>
					</Box>
					<Box>
						<BaseButton
							variant="contained"
							radius="7px"
							fontSize={16}
							fontWeight={800}
							bgcolor="#791377"
							startIcon={<AddIcon />}
							padding="calc(var(--basic-padding)/3) calc(var(--basic-padding) * 1.5)"
							sx={{
								width: { mobile: "-webkit-fill-available", miniTablet: "auto" },
							}}
							onClick={handleNavigateToAddSystemUserForm}
						>
							<Typography
								variant={"button"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"inherit"}
								textTransform={"inherit"}
							>
								Add
							</Typography>
						</BaseButton>
					</Box>
				</Stack>
				<Box
					marginBlock={"calc(var(--basic-margin)/2) calc(var(--basic-margin))"}
				>
					<form>
						<BaseFieldSet>
							<BaseLabel colour="#505050" fontWeight={500} fontSize="15px">
								Search
							</BaseLabel>
							<BaseInput
								name="filter"
								colour="#666666"
								bgcolor="#FFFFFF"
								fontWeight={400}
								value={filter}
								placeholder="Enter First Name"
								onChange={(e) => handleChange(e)}
								startAdornment={
									<Box component={"div"} className="search-icon-box">
										<SearchIcon />
									</Box>
								}
							/>
						</BaseFieldSet>
					</form>
				</Box>
				<BaseTable
					headers={headers}
					rows={filteredUsers as Record<any, any>[]}
					location="all-system-users"
					handleItemClick={handleCallToActionClick}
				/>
			</SystemUsersWrapper>
		</Layout>
	);
};
